import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PAIRINGS = [
  { id: 'current',             heading: 'Cormorant Garamond',   body: 'Jost',               tag: 'Current',    headingWeight: 300, bodyWeight: 300 },
  { id: 'ballet-crimson',      heading: 'Ballet',               body: 'Crimson Pro',        tag: 'Dramatic',   headingWeight: 400, bodyWeight: 300 },
  { id: 'forum-hanken',        heading: 'Forum',                body: 'Hanken Grotesk',     tag: 'Classical',  headingWeight: 400, bodyWeight: 300 },
  { id: 'petit-baskerville',   heading: 'Petit Formal Script',  body: 'Libre Baskerville',  tag: 'Romantic',   headingWeight: 400, bodyWeight: 400 },
  { id: 'italiana-raleway',    heading: 'Italiana',             body: 'Raleway',            tag: 'Roman',      headingWeight: 400, bodyWeight: 300 },
  { id: 'tenor-nanum',         heading: 'Tenor Sans',           body: 'Nanum Myeongjo',     tag: 'Refined',    headingWeight: 400, bodyWeight: 400 },
  { id: 'playfair-lato',       heading: 'Playfair Display',     body: 'Lato',               tag: 'Editorial',  headingWeight: 400, bodyWeight: 300 },
];

const PALETTES = [
  { id: 'ivory',  name: 'Warm Ivory',   bg: '#FAF7F2', heading: '#2C2520', body: '#6B5744', accent: '#B8975A' },
  { id: 'blanc',  name: 'Blanc & Noir', bg: '#FFFFFF',  heading: '#0A0A0A', body: '#4A4A4A', accent: '#B8975A' },
  { id: 'noir',   name: 'Noir & Gold',  bg: '#0C0C0C', heading: '#F5F0E8', body: '#9A8C7C', accent: '#B8975A' },
  { id: 'navy',   name: 'Navy',         bg: '#0E1829', heading: '#F0EBE1', body: '#A09888', accent: '#D4B896' },
  { id: 'sage',   name: 'Sage',         bg: '#E8EDE5', heading: '#2D3D2A', body: '#5C6E55', accent: '#B8915A' },
  { id: 'blush',  name: 'Blush',        bg: '#F5EBE4', heading: '#2C2828', body: '#7A5E58', accent: '#C49070' },
];

const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;1,300' +
  '&family=Jost:wght@300;400' +
  '&family=Forum' +
  '&family=Hanken+Grotesk:wght@300;400' +
  '&family=Petit+Formal+Script' +
  '&family=Libre+Baskerville:ital,wght@0,400;1,400' +
  '&family=Tenor+Sans' +
  '&family=Nanum+Myeongjo' +
  '&family=Ballet:opsz@16..72' +
  '&family=Crimson+Pro:ital,wght@0,300;1,300' +
  '&family=Italiana' +
  '&family=Raleway:wght@300;400' +
  '&family=EB+Garamond:ital,wght@0,400;1,400' +
  '&family=Montserrat:wght@300;400' +
  '&family=Playfair+Display:ital,wght@0,400;1,400' +
  '&family=Lato:wght@300;400' +
  '&display=swap';

const chip = (active: boolean, accent = '#B8975A'): React.CSSProperties => ({
  padding: '0.3rem 0.8rem',
  borderRadius: 4,
  border: active ? `1.5px solid ${accent}` : '1px solid #2e2e2e',
  background: active ? `${accent}22` : 'transparent',
  color: active ? accent : '#555',
  fontSize: '0.68rem',
  letterSpacing: '0.07em',
  cursor: 'pointer',
  transition: 'all 0.15s',
  whiteSpace: 'nowrap' as const,
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
});

export function FontPairingPage() {
  const [tab, setTab]                 = useState<'grid' | 'showcase'>('grid');
  const [paletteId, setPaletteId]     = useState('ivory');
  const [selectedId, setSelectedId]   = useState<string | null>(null);
  const [showcaseIndex, setShowcaseIndex] = useState(0);
  const [paused, setPaused]           = useState(false);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = GOOGLE_FONTS_URL;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  // Auto-advance showcase
  useEffect(() => {
    if (tab !== 'showcase' || paused) return;
    const timer = setInterval(() => {
      setShowcaseIndex(i => (i + 1) % PAIRINGS.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [tab, paused]);

  const gridPalette  = PALETTES.find(p => p.id === paletteId) ?? PALETTES[0];
  const current      = PAIRINGS[showcaseIndex];
  // In showcase mode each slide gets its own palette, cycling through all of them
  const palette      = tab === 'showcase' ? PALETTES[showcaseIndex % PALETTES.length] : gridPalette;
  const accentGrad   = `linear-gradient(90deg, transparent, ${palette.accent}, transparent)`;

  return (
    <div style={{ minHeight: '100vh', background: '#111', fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column' }}>

      {/* ── Control bar ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: '#161616',
        borderBottom: '1px solid #222',
        padding: '0.65rem 1.5rem',
        display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center',
      }}>
        {/* Tabs */}
        <button style={chip(tab === 'grid')} onClick={() => setTab('grid')}>Grid</button>
        <button style={chip(tab === 'showcase')} onClick={() => setTab('showcase')}>Showcase</button>

        <div style={{ width: 1, height: 16, background: '#2a2a2a', margin: '0 0.5rem' }} />

        {/* Palettes — only in grid mode; showcase auto-cycles */}
        {tab === 'grid' && (
          <>
            <span style={{ color: '#333', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginRight: '0.1rem' }}>Palette</span>
            {PALETTES.map(p => (
              <button key={p.id} style={chip(paletteId === p.id, p.accent)} onClick={() => setPaletteId(p.id)}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.accent, flexShrink: 0 }} />
                {p.name}
              </button>
            ))}
          </>
        )}

        {selectedId && tab === 'grid' && (
          <>
            <div style={{ width: 1, height: 16, background: '#2a2a2a', margin: '0 0.5rem' }} />
            <button style={chip(false)} onClick={() => setSelectedId(null)}>✕ Clear</button>
          </>
        )}

        {tab === 'showcase' && (
          <>
            <div style={{ width: 1, height: 16, background: '#2a2a2a', margin: '0 0.5rem' }} />
            <button style={chip(paused)} onClick={() => setPaused(p => !p)}>
              {paused ? '▶ Play' : '⏸ Pause'}
            </button>
          </>
        )}
      </div>

      {/* ── Grid view ── */}
      {tab === 'grid' && (
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: '1.25rem',
          padding: '1.5rem',
        }}>
          {PAIRINGS.map(pair => {
            const isSelected = selectedId === pair.id;
            return (
              <div
                key={pair.id}
                onClick={() => setSelectedId(isSelected ? null : pair.id)}
                style={{
                  cursor: 'pointer',
                  borderRadius: 6,
                  overflow: 'hidden',
                  outline: isSelected ? `2px solid ${palette.accent}` : '2px solid transparent',
                  boxShadow: isSelected ? `0 0 20px ${palette.accent}33` : '0 2px 16px rgba(0,0,0,0.5)',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{
                  background: palette.bg,
                  padding: '2.5rem 1.75rem 2rem',
                  minHeight: 360,
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', textAlign: 'center', position: 'relative',
                }}>
                  <span style={{
                    position: 'absolute', top: 12, right: 12,
                    fontSize: '0.52rem', letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: palette.accent, fontFamily: `${pair.body}, sans-serif`,
                    fontWeight: pair.bodyWeight, opacity: 0.75,
                  }}>
                    {pair.tag}
                  </span>

                  <p style={{ fontFamily: `${pair.body}, sans-serif`, fontWeight: pair.bodyWeight, fontSize: '0.58rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: palette.body, margin: '0 0 1.25rem' }}>
                    Save the Date
                  </p>
                  <div style={{ height: 1, width: 50, background: accentGrad, marginBottom: '1.25rem' }} />

                  <p style={{ fontFamily: `${pair.heading}, serif`, fontWeight: pair.headingWeight, fontSize: 'clamp(1.6rem, 5vw, 2.4rem)', lineHeight: 1.05, color: palette.heading, letterSpacing: '-0.01em', margin: 0 }}>
                    Emma
                  </p>
                  <p style={{ fontFamily: `${pair.heading}, serif`, fontStyle: 'italic', fontWeight: pair.headingWeight, fontSize: '1.1rem', color: palette.accent, margin: '0.15em 0' }}>
                    &amp;
                  </p>
                  <p style={{ fontFamily: `${pair.heading}, serif`, fontWeight: pair.headingWeight, fontSize: 'clamp(1.6rem, 5vw, 2.4rem)', lineHeight: 1.05, color: palette.heading, letterSpacing: '-0.01em', margin: '0 0 1.25rem' }}>
                    James
                  </p>

                  <div style={{ height: 1, width: 80, background: accentGrad, marginBottom: '1.25rem' }} />
                  <p style={{ fontFamily: `${pair.body}, sans-serif`, fontWeight: pair.bodyWeight, fontSize: '0.65rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: palette.body, margin: 0 }}>
                    17 · June · 2026
                  </p>
                </div>

                <div style={{ background: '#1a1a1a', padding: '0.65rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ color: '#ccc', fontSize: '0.68rem', margin: 0, letterSpacing: '0.04em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {pair.heading}
                    </p>
                    <p style={{ color: '#444', fontSize: '0.62rem', margin: '0.15rem 0 0', letterSpacing: '0.04em' }}>
                      {pair.body}
                    </p>
                  </div>
                  {isSelected && (
                    <span style={{ color: palette.accent, fontSize: '0.55rem', letterSpacing: '0.15em', textTransform: 'uppercase', flexShrink: 0 }}>
                      ✓ Selected
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Showcase view ── */}
      {tab === 'showcase' && (
        <div style={{ flex: 1, background: palette.bg, transition: 'background 0.8s ease', display: 'flex', flexDirection: 'column' }}>
          {/* Scrollable content area */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '3rem 2rem 0', overflow: 'hidden' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
            >
              {/* Save the Date */}
              <p style={{
                fontFamily: `${current.body}, sans-serif`,
                fontWeight: current.bodyWeight,
                fontSize: '0.72rem', letterSpacing: '0.32em',
                textTransform: 'uppercase', color: palette.body,
                marginBottom: '2.25rem',
              }}>
                Save the Date
              </p>

              {/* Top line */}
              <div style={{ height: 1, width: 70, background: accentGrad, marginBottom: '2.25rem' }} />

              {/* Emma */}
              <p style={{
                fontFamily: `${current.heading}, serif`,
                fontWeight: current.headingWeight,
                fontSize: 'clamp(3.5rem, 8vw, 6.5rem)',
                lineHeight: 1.0, color: palette.heading,
                letterSpacing: '-0.01em', margin: 0,
              }}>
                Emma
              </p>

              {/* & */}
              <p style={{
                fontFamily: `${current.heading}, serif`,
                fontStyle: 'italic',
                fontWeight: current.headingWeight,
                fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)',
                color: palette.accent, margin: '0.2em 0',
              }}>
                &amp;
              </p>

              {/* James */}
              <p style={{
                fontFamily: `${current.heading}, serif`,
                fontWeight: current.headingWeight,
                fontSize: 'clamp(3.5rem, 8vw, 6.5rem)',
                lineHeight: 1.0, color: palette.heading,
                letterSpacing: '-0.01em', margin: '0 0 2.25rem',
              }}>
                James
              </p>

              {/* Bottom line */}
              <div style={{ height: 1, width: 130, background: accentGrad, marginBottom: '2.25rem' }} />

              {/* Date */}
              <p style={{
                fontFamily: `${current.body}, sans-serif`,
                fontWeight: current.bodyWeight,
                fontSize: '0.75rem', letterSpacing: '0.28em',
                textTransform: 'uppercase', color: palette.body,
              }}>
                17 · June · 2026
              </p>

              {/* Font label — inside the fade so it never flashes */}
              <div style={{ marginTop: '2.5rem' }}>
                <p style={{ color: palette.accent, fontSize: '0.68rem', letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: 'system-ui', margin: 0 }}>
                  {current.heading} · {current.body}
                </p>
                <p style={{ color: palette.body, fontSize: '0.55rem', letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: 'system-ui', opacity: 0.5, margin: '0.3rem 0 0' }}>
                  {current.tag}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
          </div>

          {/* ── Progress dots — outside overflow area, never clipped ── */}
          <div style={{
            padding: '1rem 0 1.5rem',
            display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.4rem',
            flexShrink: 0,
          }}>
            {PAIRINGS.map((p, i) => (
              <button
                key={p.id}
                onClick={() => { setShowcaseIndex(i); setPaused(true); }}
                style={{
                  width: i === showcaseIndex ? 22 : 6,
                  height: 6, borderRadius: 3, border: 'none', padding: 0, cursor: 'pointer',
                  background: i === showcaseIndex ? palette.accent : `${palette.accent}40`,
                  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
